import { Suspense, lazy, useEffect, useState, useRef, useCallback } from "react";
import { Outlet, Route, Routes, useNavigate } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import { I18nextProvider } from "react-i18next";
import { analytics, postMessageClose, ToastContainer } from "mfe-lib-bfo-shared";
import { useLoader } from "mfe-lib-bfo-shared";
import { ChannelRedirectGate } from "./functions/ChannelRedirectGate";
import { useTokenRefresher } from "./functions/session/useTokenRefresher";
import {
    sessionManager,
    useSessionTimeout,
    initUserActivityListener,
    useWebViewResume,
    sessionStorageService,
} from "mfe-lib-bfo-shared/session";
import { SessionTimeoutDialog } from "./functions/session/SessionTimeoutDialog";
import { ErrorPage } from "./ErrorPage";
import { useRegisterPostLoginDecorators } from "./functions/query/useRegisterPostLoginDecorators";
import { SessionExpiredDialog } from "./functions/session/SessionExpiredDialog";
import { AppConfig } from "./config/app.config";
// Routing is the host's domain: these paths are where the federated pages
// are mounted, matching each remote's documented route.
export const STATUS_TRACKER_PATH = "/app-status-tracker";
export const STATUS_TRACKER_PATH_WILDCARD = "/app-status-tracker/*";
export const CASHLITE_PATH = "/cashlite";
export const CASHLITE_PATH_WILDCARD = "/cashlite/*";
export const BALANCE_TRANSFER_PATH_WILDCARD = "/balance-transfer/*";
export const BALANCE_TRANSFER_PATH = "/balance-transfer";
export const HANDSHAKE_DUMMY_PATH = "/handshake-dummy";

// The remotes expose their pages as default exports — the shape
// @originjs/vite-plugin-federation's wrapDefault interop expects.
const StatusTrackerPage = lazy(() => import("appStatusTracker/StatusTrackerPage"));
const CashlitePage = lazy(() => import("cashlite/CashlitePage"));
const BalanceTransferPage = lazy(() => import("balanceTransfer/BalanceTransferPage"));
const HandshakeDummyPage = lazy(() => import("statusTracker/HandshakeDummyPage"));

function NotFound() {
    return (
        <div style={{ padding: "24px" }}>
            <h1>Page not found</h1>
        </div>
    );
}

function ErrorFallback({ error, resetErrorBoundary }) {
    return (
        <div className="error-container">
            <p>Failed to load the remote module. It might be offline.</p>
            <p>{`Error: ${error}`}</p>
            <button onClick={resetErrorBoundary}>Retry</button>
        </div>
    );
}

function AuthenticatedLayout() {
    const [showTimeoutDialog, setShowTimeoutDialog] = useState(false);
    const [showExpiredDialog, setShowExpiredDialog] = useState(false);

    const [remainingSeconds, setRemainingSeconds] = useState(0);

    const resolver = useRef<((value: boolean) => void) | null>(null);

    // _tokenName is kept just in case for future enhancement for different prompter for each token
    const idlePrompter = useCallback((_tokenName: string, seconds: number): Promise<boolean> => {
        setRemainingSeconds(seconds);

        setShowTimeoutDialog(true);

        return new Promise<boolean>((resolve) => {
            resolver.current = resolve;
        });
    }, []);

    const continueSession = useCallback(() => {
        setShowTimeoutDialog(false);

        resolver.current?.(true);
        resolver.current = null;
    }, []);

    const handleTimeout = useCallback(() => {
        setShowTimeoutDialog(false);
        setShowExpiredDialog(true);
    }, []);

    const handleLogout = useCallback(() => {
        // sessionManager.logout();
        postMessageClose();
    }, []);

    useEffect(() => {
        return sessionManager.subscribe((event) => {
            if (event !== "logout") {
                return;
            }

            setShowTimeoutDialog(false);
            setShowExpiredDialog(true);

            resolver.current?.(false);
            resolver.current = null;
        });
    }, []);

    useSessionTimeout(idlePrompter);

    useEffect(() => {
        return initUserActivityListener();
    }, []);

    useWebViewResume(() => {
        sessionStorageService.checkOtherWebView();
    });

    return (
        <>
            <SessionTimeoutDialog
                open={showTimeoutDialog}
                setOpen={setShowTimeoutDialog}
                initialSeconds={remainingSeconds}
                onContinue={continueSession}
                onTimeout={handleTimeout}
            />

            <SessionExpiredDialog open={showExpiredDialog} setOpen={setShowExpiredDialog} onLogout={handleLogout} />

            <Outlet />
        </>
    );
}

function SuspenseFallback() {
    const { showLoader, hideLoader } = useLoader();

    useEffect(() => {
        showLoader();

        return () => {
            hideLoader();
        };
    }, [showLoader, hideLoader]);

    return null;
}

type AppProps = {
    i18n: typeof import("i18next").default;
};

export function App({ i18n }: AppProps) {
    // Load the environment-specific API origin before creating HTTP connectors.
    useRegisterPostLoginDecorators(i18n);

    // Initialize token manager
    useTokenRefresher();

    const navigate = useNavigate();

    useEffect(() => {
        analytics.trackEvent("init", {});
    }, []);

    return (
        <div className="app-shell">
            <main className="app-main">
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <Suspense fallback={<SuspenseFallback />}>
                        <I18nextProvider i18n={i18n}>
                            <ToastContainer onNavigate={(target) => navigate(target.path, { state: target.state })} />
                            <Routes>
                                {/* Pre-login: channel handshake happens before any session exists,
                                    so it's mounted outside AuthenticatedLayout like /login. */}
                                <Route
                                    path={HANDSHAKE_DUMMY_PATH}
                                    element={<HandshakeDummyPage VITE_API_BASE_URL={AppConfig.getBaseUrl()} />}
                                />
                                {/* AuthenticatedLayout means the user has logged in */}
                                <Route path={"/error/*"} element={<ErrorPage />} />
                                <Route element={<AuthenticatedLayout />}>
                                    <Route
                                        path={CASHLITE_PATH_WILDCARD}
                                        element={
                                            <ChannelRedirectGate i18n={i18n}>
                                                <CashlitePage
                                                    VITE_S3_BUCKET_URL={AppConfig.getS3BucketUrl()}
                                                    VITE_CPL_URL={AppConfig.getCplUrl()}
                                                />
                                            </ChannelRedirectGate>
                                        }
                                    />
                                    <Route
                                        path={BALANCE_TRANSFER_PATH_WILDCARD}
                                        element={
                                            <ChannelRedirectGate i18n={i18n}>
                                                <BalanceTransferPage
                                                    VITE_S3_BUCKET_URL={AppConfig.getS3BucketUrl()}
                                                    VITE_CPL_URL={AppConfig.getCplUrl()}
                                                />
                                            </ChannelRedirectGate>
                                        }
                                    />
                                    <Route
                                        path={STATUS_TRACKER_PATH_WILDCARD}
                                        element={
                                            <ChannelRedirectGate i18n={i18n}>
                                                <StatusTrackerPage VITE_S3_BUCKET_URL={AppConfig.getS3BucketUrl()} />
                                            </ChannelRedirectGate>
                                        }
                                    />
                                    <Route path="*" element={<NotFound />} />
                                </Route>
                            </Routes>
                        </I18nextProvider>
                    </Suspense>
                </ErrorBoundary>
            </main>
        </div>
    );
}
